import * as React from "react";
import { isNullOrUndefined } from "util";
import usersQuery from "./user-search.query.graphql";
import {
  FlagProps,
  withShowFlag,
  compose,
  ChildProps,
  graphql
} from "@atlassiansox/microscopekit";
import {
  checkValidName,
  displayUserInfo,
  getUserFullName,
  parseUsername
} from "./helpers";
import { logger } from "../../core";
import { User } from "../../typings";

const AkSpinner = require("@atlaskit/spinner").default;
const AkSingleSelect = require("@atlaskit/single-select").default;
const AkMultiSelect = require("@atlaskit/multi-select").default;

type users = { users: User[] };
type Option = {
  content: JSX.Element | string;
  value: string | number;
  filterValues: string[];
};
export interface Props {
  query?: string;
  defaultUsers?: User[];

  placeholder?: string;
  onChange?: (selectedUsers: string[]) => void;

  label?: string;
  disabled?: boolean;
  required?: boolean;
  invalidMessage?: string;
  multiSelect?: boolean;
}

type UserSearchProps = ChildProps<Props & FlagProps, users>;

interface State {
  curInput: string;
  showSpinner?: boolean;
  querying: boolean;
  invalidMessage: string;
}

export class UserSearch extends React.Component<UserSearchProps, State> {
  constructor(props: UserSearchProps) {
    super(props);
    this.state = {
      curInput: "",
      querying: false,
      invalidMessage: ""
    };
  }

  searchUsers = async (input: string) => {
    input = input.trim();

    let un = parseUsername(input);
    if (un !== "") {
      input = un;
    }

    let errs = checkValidName(input);
    if (errs) {
      this.setState({ invalidMessage: errs });
      return;
    }

    if (input) {
      this.setState({
        querying: true,
        showSpinner: true,
        curInput: input,
        invalidMessage: ""
      });
      let result = await this.props!.data!.refetch({
        query: input,
        skip: false
      });

      if (result && result.errors) {
        let errMsg = "";
        result.errors.map(err => {
          errMsg += err.message + " ";
        });
        errMsg = errMsg.trim();

        this.props.showFlag({ type: "error", title: "Error", message: errMsg });
        logger.error(errMsg);
      }
      this.setState({ querying: false, showSpinner: false });
    }
  };

  selectUser = (event: any) => {
    if (this.props.onChange) {
      let selectedNames = event.item ? [event.item] : event.items;
      this.props.onChange(selectedNames.map((option: Option) => option.value));
    }
  };

  render() {
    let {
      defaultUsers,
      disabled,
      required,
      placeholder,
      invalidMessage,
      multiSelect,
      label
    } = this.props;
    let users = this.props.data!.users ? this.props.data!.users : [];

    let defaultSelected: Option[] = [];
    let userOptions: Option[] = [];

    const getUserOption = (user: User) => {
      const filterValues = [
        user.username || user.id,
        user.displayName,
        user.email,
        getUserFullName(user)
      ].filter(function(value): value is string {
        return !isNullOrUndefined(value);
      });
      return {
        content: displayUserInfo(user),
        value: user.username || user.id,
        filterValues
      };
    };

    users!.forEach(user => {
      userOptions.push(getUserOption(user));
    });
    if (defaultUsers) {
      defaultUsers.forEach(user => {
        defaultSelected.push(getUserOption(user));
      });
    }

    let errMsg = "";
    if (invalidMessage) {
      errMsg += invalidMessage + " ";
    }
    if (this.state.invalidMessage) {
      errMsg += this.state.invalidMessage;
    }
    errMsg = errMsg.trim();

    if (multiSelect) {
      return (
        <AkMultiSelect
          hasAutocomplete
          shouldFitContainer
          filterValue={this.state.curInput}
          isDisabled={disabled}
          placeholder={placeholder}
          isInvalid={errMsg}
          invalidMessage={errMsg}
          isRequired={required}
          onSelectedChange={this.selectUser}
          noMatchesFound={
            this.state.showSpinner ? (
              <AkSpinner size="small" isCompleting={false} />
            ) : (
              undefined
            )
          }
          onFilterChange={this.searchUsers}
          label={label}
          items={[{ items: !this.state.querying ? userOptions : [] }]}
          defaultSelected={defaultSelected}
          selectedItem={defaultSelected}
        />
      );
    }

    return (
      <AkSingleSelect
        hasAutocomplete
        shouldFitContainer
        filterValue={this.state.curInput}
        isDisabled={disabled}
        placeholder={placeholder}
        isInvalid={errMsg}
        invalidMessage={errMsg}
        isRequired={required}
        onSelected={this.selectUser}
        noMatchesFound={
          this.state.showSpinner ? (
            <AkSpinner size="small" isCompleting={false} />
          ) : (
            undefined
          )
        }
        onFilterChange={this.searchUsers}
        label={label}
        items={[{ items: !this.state.querying ? userOptions : [] }]}
        defaultSelected={defaultSelected[0]}
        selectedItem={defaultSelected[0]}
      />
    );
  }
}

export const UserSearchField = compose(
  graphql(usersQuery, {
    options: () => ({
      errorPolicy: "all",
      context: {
        debounceKey: "UserSearchProps"
      },
      variables: {
        query: "",
        skip: true
      }
    })
  }),
  withShowFlag
)(UserSearch);
UserSearchField.displayName = "UserSearchField";
